/**
 * Authentication service for web app - matches mobile app behavior
 * Handles Google Sign-In with Firebase Auth and JWT token management
 */

import { auth, googleProvider, signInWithPopup, signOut, onAuthStateChanged, type User } from './firebase';

// Types
interface TokenCache {
  token: string | null;
  expiry: number | null;
}

interface UserData {
  email: string;
  displayName: string | null;
  photoURL: string | null;
}

class AuthService {
  private tokenCache: TokenCache = { token: null, expiry: null };
  private authStateCallbacks: ((user: User | null) => void)[] = [];

  constructor() {
    // Listen to auth state changes
    onAuthStateChanged(auth, (user) => {
      console.log('🔔 [AuthService] Auth state changed:', user?.email || 'No user');
      this.authStateCallbacks.forEach(callback => callback(user));
      
      if (!user) {
        this.clearTokenCache();
        this.clearStoredTokens();
      }
    });
  }

  /**
   * Register a callback for auth state changes
   */
  onAuthStateChange(callback: (user: User | null) => void): () => void {
    this.authStateCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.authStateCallbacks.indexOf(callback);
      if (index > -1) {
        this.authStateCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Check if a JWT token is about to expire
   */
  private tokenNeedsRenewal(token: string, thresholdMinutes: number = 10): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return true;

      const payload = JSON.parse(atob(parts[1]));
      if (!payload.exp) return true;

      const thresholdMs = thresholdMinutes * 60 * 1000;
      return payload.exp * 1000 - Date.now() < thresholdMs;
    } catch (error) {
      console.log('Error checking token expiration:', error);
      return true;
    }
  }

  /**
   * Clear the in-memory token cache
   */
  clearTokenCache(): void {
    console.log('🧹 [AuthService] Clearing token cache...');
    this.tokenCache = { token: null, expiry: null };
  }

  /**
   * Clear stored tokens from localStorage
   */
  private clearStoredTokens(): void {
    const email = localStorage.getItem('userEmail');
    if (email) {
      localStorage.removeItem(`googleToken_${email}`);
    }
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userData');
  }

  /**
   * Get current authenticated user
   */
  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!auth.currentUser;
  }

  /**
   * Get user data
   */
  getUserData(): UserData | null {
    const user = auth.currentUser;
    if (!user) return null;

    return {
      email: user.email || '',
      displayName: user.displayName,
      photoURL: user.photoURL
    };
  }

  /**
   * Get a valid JWT token - main method used by API calls
   */
  async getGoogleToken(): Promise<string> {
    try {
      console.log('getGoogleToken: Starting token retrieval');

      // Check if there's a token in cache and if it's still valid
      const now = Date.now();
      if (this.tokenCache.token && this.tokenCache.expiry && this.tokenCache.expiry > now) {
        console.log('getGoogleToken: Cached token available');

        if (this.tokenNeedsRenewal(this.tokenCache.token)) {
          console.log('getGoogleToken: Cached token about to expire, renewing');
        } else {
          console.log('getGoogleToken: Using valid cached token');
          return this.tokenCache.token;
        }
      }

      console.log('getGoogleToken: Getting new token');

      // Get email from current user
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        throw new Error('SIGN_IN_REQUIRED');
      }

      const email = currentUser.email;

      // Check localStorage for cached token
      const cachedToken = localStorage.getItem(`googleToken_${email}`);
      if (cachedToken && !this.tokenNeedsRenewal(cachedToken)) {
        console.log('getGoogleToken: Token found in localStorage and valid');
        this.tokenCache = {
          token: cachedToken,
          expiry: now + 30 * 60 * 1000,
        };
        return cachedToken;
      }

      console.log('getGoogleToken: Getting fresh token from Firebase');

      // Get fresh token from Firebase
      const idToken = await currentUser.getIdToken(true); // Force refresh

      if (!idToken) {
        throw new Error('No idToken obtained from Firebase');
      }

      console.log('getGoogleToken: Token obtained successfully');

      // Save in cache
      this.tokenCache = {
        token: idToken,
        expiry: now + 55 * 60 * 1000, // 55 minutes
      };

      // Save in localStorage
      localStorage.setItem(`googleToken_${email}`, idToken);
      localStorage.setItem('userEmail', email);

      return idToken;
    } catch (error) {
      console.log('getGoogleToken: Error:', error);
      
      if (error instanceof Error && error.message === 'SIGN_IN_REQUIRED') {
        throw error;
      }
      
      throw new Error('Could not obtain authentication token');
    }
  }

  /**
   * Sign in with Google
   */
  async signInWithGoogle(): Promise<UserData> {
    try {
      console.log('🔑 [AuthService] Starting Google Sign-In');

      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      if (!user || !user.email) {
        throw new Error('No user data received from Google');
      }

      console.log('✅ [AuthService] Google Sign-In successful:', user.email);

      const userData: UserData = {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL
      };

      // Store user data
      localStorage.setItem('userEmail', user.email);
      localStorage.setItem('userData', JSON.stringify(userData));

      // Get and cache the token
      await this.getGoogleToken();

      return userData;
    } catch (error: any) {
      console.error('❌ [AuthService] Google Sign-In failed:', error);
      
      // Handle specific Google Sign-In errors
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in was cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Sign-in popup was blocked by browser');
      } else if (error.code === 'auth/network-request-failed') {
        throw new Error('Network error during sign-in');
      }
      
      throw new Error(`Sign-in failed: ${error.message}`);
    }
  }

  /**
   * Sign out
   */
  async signOut(): Promise<void> {
    try {
      console.log('🚪 [AuthService] Signing out...');
      
      this.clearTokenCache();
      this.clearStoredTokens();
      
      await signOut(auth);
      
      console.log('✅ [AuthService] Sign out successful');
    } catch (error) {
      console.error('❌ [AuthService] Sign out failed:', error);
      throw error;
    }
  }

  /**
   * Check and refresh token if needed
   */
  async refreshTokenIfNeeded(): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const token = await currentUser.getIdToken(false); // Don't force refresh
      
      if (this.tokenNeedsRenewal(token)) {
        console.log('🔄 [AuthService] Token needs refresh, getting new one');
        await this.getGoogleToken();
      }
    } catch (error) {
      console.error('❌ [AuthService] Token refresh failed:', error);
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;